/**
 * The core offset (docs/archive/OPEN_WORLD.md §4.5): where a stamped zone's authored map (the village) sits
 * in the map the World plays on. Data files keep village-local coordinates (training spots, the
 * heights spec, the layout); a reader adds the origin through this one helper, so the same rules
 * and tests work with region maps on (the village at the region's `core`) and off (origin 0, 0).
 *
 * `regionMap` (region/zone.ts) marks each region map it makes; any other map is its own core.
 */
import type { GameMap, Point } from '../map';

const ZERO: Point = { x: 0, y: 0 };
const origins = new WeakMap<GameMap, Point>();

/** Records that `m`'s authored core has its tile (0, 0) at `at` (region/zone.ts). */
export function markCore(m: GameMap, at: Point): void {
  origins.set(m, { x: at.x, y: at.y });
}

/** Where the authored core's tile (0, 0) lies in `m`: the region's `core`, or (0, 0) for an authored map. */
export const coreOf = (m: GameMap): Point => origins.get(m) ?? ZERO;

/** A village-local point in `m`'s coordinates. */
export function fromCore(m: GameMap, p: Point): Point;
export function fromCore(m: GameMap, x: number, y: number): Point;
export function fromCore(m: GameMap, a: Point | number, b?: number): Point {
  const o = coreOf(m);
  return typeof a === 'number' ? { x: a + o.x, y: b! + o.y } : { x: a.x + o.x, y: a.y + o.y };
}

/** A point of `m` in the core's own (village-local) coordinates. */
export function toCore(m: GameMap, x: number, y: number): Point {
  const o = coreOf(m);
  return { x: x - o.x, y: y - o.y };
}
